/**
 * termRefStore — lire et créer les termes d'un champ `term-ref`.
 *
 * Module à part, et non des fonctions exportées depuis `TermRefEditor.jsx` :
 * deux surfaces s'en servent (le contrôle du formulaire des champs et
 * l'édition EN PLACE dans le canevas), et un export non-composant depuis un
 * `.jsx` casse le Fast Refresh de Vite — l'écran disparaît sans la moindre
 * trace côté build.
 *
 * LES CATÉGORIES SONT CELLES DU SITE : la liste vient de la collection
 * decoupled du vocabulaire filtrée sur `field_site`, et un terme créé ici naît
 * dans CE site (`siteIds`), jamais dans le fonds commun. Un type sans site n'a
 * rien sur quoi filtrer : il voit alors tout le vocabulaire.
 */

/** Le vocabulaire d'un champ — le premier déclaré, ou null. */
export const vocabularyOf = (vocabularies) =>
  (Array.isArray(vocabularies) && vocabularies.length > 0 ? vocabularies[0] : null)

/** La valeur stockée pour un terme : `{tid, vocabulary, name}`. */
export const toTermValue = (term, vocabulary) => ({
  tid: Number(term.id ?? term.tid),
  vocabulary,
  name: term.name,
})

/**
 * Les termes du vocabulaire, bornés au site quand il y en a un.
 *
 * DEUX formes de ligne, selon le DROIT D'ÉCRITURE de qui demande : une ligne
 * modifiable arrive ENVELOPPÉE (`{item_url, submission_url, data: {...}}`), une
 * ligne en lecture seule a ses champs à la racine. Un rédacteur qui peut créer
 * des catégories reçoit donc la première — ne lire que la seconde donnait une
 * liste vide, sans erreur. Normalisation stricte par-dessus : le display masque
 * les champs vides, donc une ligne peut arriver SANS `name`.
 *
 * @returns {Promise<Array<{id: number, name: string}>>} vide en cas d'échec.
 */
export const loadSiteTerms = async (vocabulary, siteId = null) => {
  if (!vocabulary) return []
  const params = new URLSearchParams({ display: 'decoupled' })
  if (siteId) {
    params.set('filter[0][f]', 'field_site')
    params.set('filter[0][v]', String(siteId))
  }
  try {
    const res = await fetch(
      `/decoupled-api/taxonomy_term/${encodeURIComponent(vocabulary)}/collection?${params}`,
      { credentials: 'include' },
    )
    if (!res.ok) return []
    const rows = await res.json()
    return (Array.isArray(rows) ? rows : [])
      .map((row) => {
        const r = row?.data ?? row
        return { id: Number(r?.id ?? r?.tid), name: typeof r?.name === 'string' ? r.name : '' }
      })
      .filter((r) => Number.isFinite(r.id) && r.name !== '')
  } catch {
    return []
  }
}

/**
 * Crée le terme absent, dans le site du champ.
 *
 * Pourquoi la création se fait ici : `auto_create` est une affaire de widget de
 * formulaire Drupal, alors que l'écriture decoupled fait `$entity->set()` et
 * n'accepte donc que des TID. Le terme doit exister avant d'être référencé.
 *
 * @returns {Promise<{id: number, name: string}>}
 * @throws {Error} porteur du message du serveur (422 : nom déjà pris sur ce
 *   site ; 403 : pas le droit de créer dans ce vocabulaire ou ce groupe).
 */
export const createSiteTerm = async (vocabulary, name, siteId = null) => {
  const body = { data: { name } }
  // Sans ceci, la catégorie naîtrait commune à tous les sites — et
  // n'apparaîtrait plus dans cette liste, qui ne montre que le site.
  if (siteId) body.data.siteIds = [Number(siteId)]
  let res
  let payload = null
  try {
    res = await fetch(
      // `display=decoupled` n'est pas décoratif : sans lui, 400 « Missing
      // display parameter » — c'est le display qui porte le droit d'écriture.
      `/decoupled-api/save?display=decoupled&entity_type=taxonomy_term&bundle=${encodeURIComponent(vocabulary)}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    )
    payload = await res.json().catch(() => null)
  } catch {
    throw new Error('Création impossible — réseau ou session.')
  }
  if (!res.ok || !payload?.id) {
    throw new Error(payload?.status?.message || payload?.message || `Création refusée (${res.status}).`)
  }
  return { id: Number(payload.id), name }
}

/** Les termes dont le nom contient la saisie (casse ignorée). */
export const matchTerms = (terms, typed) => {
  const needle = typed.trim().toLowerCase()
  if (!needle) return terms
  return terms.filter((t) => t.name.toLowerCase().includes(needle))
}

/** Le terme dont le nom EST la saisie, ou undefined. */
export const exactTerm = (terms, typed) => {
  const needle = typed.trim().toLowerCase()
  if (!needle) return undefined
  return terms.find((t) => t.name.trim().toLowerCase() === needle)
}
